import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "ftm_admin_token";
const CHUNK_SIZE = 2 * 1024 * 1024;

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));

const auth = () => ({ Authorization: `Bearer ${getToken()}` });

export const formatApiError = (e) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
    return e?.message || "Errore imprevisto";
};

export const login = async (email, password) => {
    const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
    setToken(data.token);
    return data.user;
};

export const forgotPassword = async (email) => (await axios.post(`${API}/api/auth/forgot-password`, { email })).data;
export const resetPassword = async (token, new_password) => (await axios.post(`${API}/api/auth/reset-password`, { token, new_password })).data;
export const changePassword = async (current_password, new_password) =>
    (await axios.post(`${API}/api/auth/change-password`, { current_password, new_password }, { headers: auth() })).data;

export const me = async () => {
    const { data } = await axios.get(`${API}/api/auth/me`, { headers: auth() });
    return data;
};

export const uploadAttachment = async ({ file, product, label, onProgress }) => {
    const init = await axios.post(
        `${API}/api/admin/uploads/init`,
        { filename: file.name, content_type: file.type, size: file.size },
        { headers: auth() },
    );
    const uploadId = init.data.upload_id;
    const total = Math.ceil(file.size / CHUNK_SIZE);
    for (let i = 0; i < total; i += 1) {
        const form = new FormData();
        form.append("chunk", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
        await axios.put(`${API}/api/admin/uploads/${uploadId}/chunk/${i}`, form, { headers: auth() });
        onProgress?.(Math.round(((i + 1) / total) * 90));
    }
    const { data } = await axios.post(
        `${API}/api/admin/uploads/${uploadId}/complete`,
        { product_key: product.key, group: product.group, product_name: product.name, label },
        { headers: auth() },
    );
    onProgress?.(100);
    return data;
};

export const deleteAttachment = (productKey, attachmentId) =>
    axios.delete(`${API}/api/admin/products/${productKey}/attachments/${attachmentId}`, { headers: auth() });

export const renameAttachment = (productKey, attachmentId, label) => {
    const form = new FormData();
    form.append("label", label);
    return axios.patch(`${API}/api/admin/products/${productKey}/attachments/${attachmentId}`, form, { headers: auth() });
};

export const reorderAttachments = (productKey, ids) =>
    axios.post(`${API}/api/admin/products/${productKey}/attachments/reorder`, { ids }, { headers: auth() });

export const editProduct = async (product, fields) => {
    const { data } = await axios.patch(`${API}/api/admin/products/${product.key}`, { group: product.group, product_name: product.name, ...fields }, { headers: auth() });
    return data;
};

export const createProduct = async (fields) => {
    const { data } = await axios.post(`${API}/api/admin/products`, fields, { headers: auth() });
    return data;
};

export const deleteProduct = (productKey) => axios.delete(`${API}/api/admin/products/${productKey}`, { headers: auth() });

export const listContacts = async (params = {}) => {
    const { data } = await axios.get(`${API}/api/admin/contacts`, { headers: auth(), params });
    return data;
};

export const deleteContact = (id) => axios.delete(`${API}/api/admin/contacts/${id}`, { headers: auth() });

export const exportContactsXlsx = async (params = {}) => {
    const res = await axios.get(`${API}/api/admin/contacts/export.xlsx`, { headers: auth(), params, responseType: "blob" });
    const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "richieste-ftm.xlsx";
    const url = URL.createObjectURL(res.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: name });
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { name, rows: Number(res.headers["x-row-count"] ?? 0) };
};

export const sendCallbackReminderNow = async () => {
    const { data } = await axios.post(`${API}/api/admin/callback-reminder/send`, {}, { headers: auth() });
    return data;
};

export const updateContact = (id, status, note) =>
    axios.patch(`${API}/api/admin/contacts/${id}`, { status, note }, { headers: auth() });

export const replyToContact = async (id, message) => {
    const { data } = await axios.post(`${API}/api/admin/contacts/${id}/reply`, { message }, { headers: auth() });
    return data;
};

export const resendNotification = async (id) => {
    const { data } = await axios.post(`${API}/api/admin/contacts/${id}/resend-notification`, {}, { headers: auth() });
    return data;
};

export const unreadCount = async () => {
    const { data } = await axios.get(`${API}/api/admin/contacts/unread-count`, { headers: auth() });
    return data.count;
};

export const sendDigestNow = async () => {
    const { data } = await axios.post(`${API}/api/admin/digest/send`, {}, { headers: auth() });
    return data;
};

// ---- Realizzazioni (lavori) ----
export const listProjects = async () => (await axios.get(`${API}/api/admin/projects`, { headers: auth() })).data;
export const createProject = async (fields) => (await axios.post(`${API}/api/admin/projects`, fields, { headers: auth() })).data;
export const updateProject = async (id, fields) => (await axios.put(`${API}/api/admin/projects/${id}`, fields, { headers: auth() })).data;
export const setProjectStatus = async (id, status) => (await axios.post(`${API}/api/admin/projects/${id}/status`, { status }, { headers: auth() })).data;
export const deleteProject = (id) => axios.delete(`${API}/api/admin/projects/${id}`, { headers: auth() });
export const reorderProjects = (ids) => axios.post(`${API}/api/admin/projects/reorder`, { ids }, { headers: auth() });
export const editProjectPhoto = async (projectId, photoId, fields) =>
    (await axios.patch(`${API}/api/admin/projects/${projectId}/photos/${photoId}`, fields, { headers: auth() })).data;
export const deleteProjectPhoto = (projectId, photoId) => axios.delete(`${API}/api/admin/projects/${projectId}/photos/${photoId}`, { headers: auth() });
export const reorderProjectPhotos = (projectId, ids) => axios.post(`${API}/api/admin/projects/${projectId}/photos/reorder`, { ids }, { headers: auth() });

// Anteprime nel pannello (<img> non può inviare header: il token viaggia in query). variant: blurred | original | enhanced
export const projectPhotoUrl = (photo, variant = "blurred") =>
    `${API}${photo.url}?variant=${variant}&v=${photo.version ?? 0}&token=${encodeURIComponent(getToken() || "")}`;

export const uploadProjectPhoto = async ({ file, projectId, onProgress }) => {
    const init = await axios.post(`${API}/api/admin/uploads/init`, { filename: file.name, content_type: file.type || "image/jpeg", size: file.size }, { headers: auth() });
    const uploadId = init.data.upload_id;
    const total = Math.ceil(file.size / CHUNK_SIZE);
    for (let i = 0; i < total; i += 1) {
        const form = new FormData();
        form.append("chunk", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
        await axios.put(`${API}/api/admin/uploads/${uploadId}/chunk/${i}`, form, { headers: auth() });
        onProgress?.(Math.round(((i + 1) / total) * 70));
    }
    onProgress?.(75); // elaborazione: miglioramento, rilevamento volti/targhe, sfocatura
    const { data } = await axios.post(`${API}/api/admin/projects/${projectId}/photos/${uploadId}`, {}, { headers: auth() });
    onProgress?.(100);
    return data;
};
